/**
 * Builds a flat CSV export for an Internal Affairs compliance run. Run and
 * report context is repeated on every finding row so the file remains useful
 * when it is filtered, sorted, or imported into another system.
 */
import { saveAs } from "file-saver";
import type {
  ComplianceResult,
  ComplianceRun,
} from "@/features/stationAdmin/api/iaComplianceApi";
import { normalizeFindings, resultVerdictChip } from "./complianceView";

export interface ComplianceReportCsvInput {
  run: ComplianceRun;
  results: ComplianceResult[];
  stationName?: string;
  /** Injectable for deterministic tests; defaults to now at call time. */
  exportedAt?: Date;
}

export interface ComplianceHistoryCsvInput {
  runs: ComplianceRun[];
  stationName?: string;
  /** Injectable for deterministic tests; defaults to now at call time. */
  exportedAt?: Date;
}

const CSV_HEADERS = [
  "Station",
  "Run ID",
  "Run Status",
  "Run Completed At",
  "Policy Documents",
  "Report ID",
  "Report Name",
  "Result Status",
  "Verdict",
  "AI Generated",
  "Advisory",
  "Compliant",
  "Confidence",
  "Report Summary",
  "Checked At",
  "Error",
  "Finding Number",
  "Finding Count",
  "Finding Severity",
  "Policy Reference",
  "Requirement",
  "Evidence",
  "Explanation",
  "Exported At",
] as const;

const HISTORY_CSV_HEADERS = [
  "Station",
  "Run ID",
  "Run Status",
  "Requested By",
  "Policy Documents",
  "Report Count",
  "Checked Count",
  "Violation Count",
  "Error Count",
  "Created At",
  "Updated At",
  "Completed At",
  "Error",
  "AI Generated",
  "Advisory",
  "Exported At",
] as const;

const ADVISORY =
  "AI-generated advisory only. Verify findings against the policy text before acting on them.";

/**
 * Prefix values that spreadsheet programs may otherwise interpret as
 * formulas. Quoting alone does not prevent formula execution in common CSV
 * consumers, including when the formula sigil follows whitespace.
 */
function neutralizeSpreadsheetFormula(value: string): string {
  return /^[\t\r\n]|^\s*[=+\-@]/u.test(value) ? `'${value}` : value;
}

/** RFC 4180-style cell: formula-safe, quote-delimited, with doubled quotes. */
function csvCell(value: unknown): string {
  const text = neutralizeSpreadsheetFormula(String(value ?? ""));
  return `"${text.replace(/"/g, '""')}"`;
}

function row(values: readonly unknown[]): string {
  return values.map(csvCell).join(",");
}

function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob(["\uFEFF", csv], {
    type: "text/csv;charset=utf-8",
  });
  saveAs(blob, filename);
}

/**
 * Build a rectangular CSV with one row per normalized finding. Reports with no
 * findings (including failed checks) still receive one context-only row.
 */
export function buildComplianceReportCsv(
  input: ComplianceReportCsvInput,
): string {
  const { run, results, stationName = "" } = input;
  const exportedAt = (input.exportedAt ?? new Date()).toISOString();
  const runValues = [
    stationName,
    run.runId,
    run.status,
    run.completedAt ?? "",
    run.policyDocNames.join(", "),
  ];

  const rows = results.flatMap((result) => {
    const verdict = resultVerdictChip(result).label;
    const findings =
      result.status === "error" ? [] : normalizeFindings(result.findings);
    const resultValues = [
      result.reportId,
      result.title || `Report ${result.reportId}`,
      result.status,
      verdict,
      "Yes",
      ADVISORY,
      result.compliant == null ? "" : String(result.compliant),
      result.confidence ?? "",
      result.summary ?? "",
      result.checkedAt,
      result.error ?? "",
    ];

    if (findings.length === 0) {
      return [
        row([
          ...runValues,
          ...resultValues,
          "",
          0,
          "",
          "",
          "",
          "",
          "",
          exportedAt,
        ]),
      ];
    }

    return findings.map((finding, index) =>
      row([
        ...runValues,
        ...resultValues,
        index + 1,
        findings.length,
        finding.severity,
        finding.policyReference,
        finding.requirement,
        finding.evidence,
        finding.explanation,
        exportedAt,
      ]),
    );
  });

  return [row(CSV_HEADERS), ...rows].join("\r\n");
}

/** Filename keyed by run id and the UTC export date. */
export function complianceReportCsvFilename(
  run: ComplianceRun,
  exportedAt: Date = new Date(),
): string {
  const date = exportedAt.toISOString().split("T")[0];
  return `ia-compliance-${run.runId}-${date}.csv`;
}

/** Build one history row per run, preserving the order supplied by the API. */
export function buildComplianceHistoryCsv(
  input: ComplianceHistoryCsvInput,
): string {
  const { runs, stationName = "" } = input;
  const exportedAt = (input.exportedAt ?? new Date()).toISOString();
  const rows = runs.map((run) =>
    row([
      stationName,
      run.runId,
      run.status,
      run.requestedBy,
      run.policyDocNames.join(", "),
      run.reportCount,
      run.checkedCount,
      run.violationCount,
      run.errorCount,
      run.createdAt,
      run.updatedAt,
      run.completedAt ?? "",
      run.error ?? "",
      "Yes",
      ADVISORY,
      exportedAt,
    ]),
  );

  return [row(HISTORY_CSV_HEADERS), ...rows].join("\r\n");
}

/** Filename keyed by the UTC export date. */
export function complianceHistoryCsvFilename(
  exportedAt: Date = new Date(),
): string {
  const date = exportedAt.toISOString().split("T")[0];
  return `ia-compliance-history-${date}.csv`;
}

/** Build and download the CSV entirely in the authorized user's browser. */
export function exportComplianceRunCsv(
  input: ComplianceReportCsvInput,
): void {
  const exportedAt = input.exportedAt ?? new Date();
  const csv = buildComplianceReportCsv({ ...input, exportedAt });
  downloadCsv(csv, complianceReportCsvFilename(input.run, exportedAt));
}

/** Build and download all loaded history rows in the authorized user's browser. */
export function exportComplianceHistoryCsv(
  input: ComplianceHistoryCsvInput,
): void {
  const exportedAt = input.exportedAt ?? new Date();
  const csv = buildComplianceHistoryCsv({ ...input, exportedAt });
  downloadCsv(csv, complianceHistoryCsvFilename(exportedAt));
}
